import { GridRowSelectionModel } from '@mui/x-data-grid-premium'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { useState } from 'react'
import { Task } from 'types'
import { useUniversalNotify } from '../notifications/useUniversalNotify'
import { useEntitiesFromSearch } from 'api/search'
import { useSyncQueryCache } from '../organizations_table/useSyncQueryCache'
import { useBatchUpdateTasks } from './useBatchUpdateTasks'

export interface UpdateItem {
  primaryKey: string
  sortKey?: string
  tableName: string
  itemId: string
  itemSortKey?: string
  fieldName: string
  newValue: any
}

export interface DynamicMassUpdateData {
  [field: string]: any
}

// --- helpers ---
const isSubtaskSK = (sk?: string) => sk?.startsWith?.('SUBTASK_')

const buildTaskUrl = (pk: string, sk: string) => {
  const isProd = import.meta.env.VITE_ENV === 'production'
  const base = isProd ? 'https://grobotic.app' : 'https://dev.grobotic.app'
  // non-subtask detail route:
  if (!isSubtaskSK(sk)) return `${base}/teamtasks/${pk}/${sk}/main`
  // subtask list view (parent from GSI1PK on your rows if you store it), or fallback:
  return `${base}/teamtasks/${pk}/${sk}/main` // adjust if you have a parentTaskSK
}

// Render up to N links (Slack mrkdwn)
const renderTaskLinksMrkdwn = (tasks: { PK: string; SK: string; name?: string }[], maxLinks = 5) => {
  const top = tasks.slice(0, maxLinks)
  const lines = top.map((t, i) => `${i + 1}. <${buildTaskUrl(t.PK, t.SK)}|${t.name || t.SK}>`)
  const overflow = tasks.length - top.length
  return { text: lines.join('\n'), overflow }
}

const handleClosePopover = (): void => {
  // Your popover close logic here...
}

const useMassUpdateTasks = (context?: string) => {
  const { mutate: batchUpdateTasks } = useBatchUpdateTasks()
  const { notifyAssignments } = useUniversalNotify()
  const { showSnackbar } = useSnackbarContext()
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const queryClient = useQueryClient()
  const { syncTasksCacheAfterBatchUpdate } = useSyncQueryCache()
  const { allTasks } = useEntitiesFromSearch()
  const handleMassUpdate = (massUpdateData: DynamicMassUpdateData, rowSelectionModel: GridRowSelectionModel): void => {
    console.log('[MassUpdate] massUpdateData:', massUpdateData)
    setIsUpdating(true)
    const updates: UpdateItem[] = []
    const exclusiveFields = ['isMarketing', 'isCreative', 'isWebsite']

    Object.entries(massUpdateData).forEach(([field, value]) => {
      const hasMeaningfulValue =
        value !== undefined &&
        (typeof value === 'string'
          ? value.trim().length > 0
          : Array.isArray(value)
          ? value.length > 0
          : typeof value === 'object' && value !== null
          ? Object.keys(value).length > 0
          : typeof value === 'boolean')

      if (!hasMeaningfulValue) return

      Array.from(rowSelectionModel.ids).forEach(compositeId => {
        const [PK, SK] = (compositeId as string).split('::')
        const newValue =
          field === 'assignees' && Array.isArray(value)
            ? value
            : Array.isArray(value) && value.length === 1
            ? value[0]
            : value

        const isExclusiveField = exclusiveFields.includes(field)

        if (isExclusiveField && newValue === true) {
          exclusiveFields.forEach(f => {
            updates.push({
              primaryKey: 'PK',
              sortKey: 'SK',
              tableName: 'TASKS_TABLE',
              itemId: PK,
              itemSortKey: SK,
              fieldName: f,
              newValue: f === field, // true for selected field, false for others
            })
          })
        } else {
          updates.push({
            primaryKey: 'PK',
            sortKey: 'SK',
            tableName: 'TASKS_TABLE',
            itemId: PK,
            itemSortKey: SK,
            fieldName: field,
            newValue,
          })
        }
      })
    })

    console.log('[MassUpdate] Final update payload:', updates)
    const updatedMap: Record<string, Task> = {}

    updates.forEach(({ itemSortKey: SK, fieldName, newValue }) => {
      const existing = allTasks.find(d => d.SK === SK)
      if (!existing) return
      if (!updatedMap[SK]) {
        updatedMap[SK] = { ...existing }
      }
      updatedMap[SK][fieldName] = newValue
    })

    const updatedTasks = Object.values(updatedMap)
    console.log('updatedTasks', updatedTasks)
    batchUpdateTasks(updates, {
      onSuccess: (_updates, variables) => {
        setIsUpdating(false)
        handleClosePopover()
        showSnackbar(
          rowSelectionModel.ids.size === 1
            ? 'Task updated successfully'
            : `${rowSelectionModel.ids.size} Tasks updated successfully`,
          'success'
        )
        syncTasksCacheAfterBatchUpdate(queryClient, updatedTasks)
        // If this batch touched the "queue" field, build user -> tasks map
        const touchedQueue = updates.some(u => u.fieldName === 'queue')
        if (touchedQueue) {
          const byUser: Record<string, { PK: string; SK: string; name?: string }[]> = {}
          const updatedBySK: Record<string, Task> = {}
          updatedTasks.forEach(t => (updatedBySK[t.SK] = t))

          updates
            .filter(u => u.fieldName === 'queue')
            .forEach(u => {
              const pk = u.itemId
              const sk = u.itemSortKey!
              const assignee = u.newValue
              if (!assignee) return
              const userId = assignee.id || assignee.user_uuid || assignee.slackId || assignee.userId
              if (!userId) return

              const t = updatedBySK[sk]
              const entry = { PK: pk, SK: sk, name: t?.name || t?.title || undefined }

              if (!byUser[userId]) byUser[userId] = []
              if (!byUser[userId].some(x => x.PK === pk && x.SK === sk)) {
                byUser[userId].push(entry)
              }
            })

          // one notification per user
          notifyAssignments({
            assignmentsByUser: byUser,
            headerText: 'You were set as Active Assignee',
            maxLinks: 5,
            sendSlack: true,
            sendApp: true,
          })
        }
      },
      onError: () => {
        setIsUpdating(false)
        showSnackbar(rowSelectionModel.ids.size === 1 ? 'Task update failed' : 'Tasks update failed', 'error')
      },
    })
  }

  return { handleMassUpdate, isUpdating }
}

export default useMassUpdateTasks
